import { Injectable, computed, signal } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';

import { environment } from '@environments/environment';

export interface FlamingoUser {
    sub?: string;
    name?: string;
    email?: string;
    roles?: string[];
    exp?: number;
    iat?: number;
}

type StorageScope = 'local' | 'session';

@Injectable({
    providedIn: 'root'
})
export class FlamingoAuthService {
    private readonly jwtHelper = new JwtHelperService();
    private readonly tokenKey = `${environment.storagePrefix}token`;
    private readonly refreshTokenKey = `${environment.storagePrefix}refresh_token`;
    private readonly navigationKey = `${environment.storagePrefix}navigation`;

    private readonly tokenSignal = signal<string | null>(
        this.getStoredToken('local') ?? this.getStoredToken('session')
    );
    readonly loggedIn = computed(() => {
        const token = this.tokenSignal();
        if (!token) {
            return false;
        }
        // Check if token is expired
        try {
            const isExpired = this.jwtHelper.isTokenExpired(token);
            if (isExpired) {
                console.warn('[FlamingoAuthService] Token is expired');
                // Clear expired token
                this.clearTokens();
                return false;
            }
            return true;
        } catch {
            console.warn('[FlamingoAuthService] Unable to validate JWT token');
            this.clearTokens();
            return false;
        }
    });
    readonly user = computed<FlamingoUser | null>(() => {
        const token = this.tokenSignal();
        if (!token) {
            return null;
        }

        try {
            // Check if token is expired before decoding
            if (this.jwtHelper.isTokenExpired(token)) {
                console.warn('[FlamingoAuthService] Token is expired, clearing');
                this.clearTokens();
                return null;
            }
            return this.jwtHelper.decodeToken(token) as FlamingoUser;
        } catch {
            console.warn('[FlamingoAuthService] Unable to decode JWT token');
            return null;
        }
    });

    getToken(): string | null {
        const token = this.tokenSignal();
        // Fallback: try to get from storage if signal is null
        if (!token) {
            const stored = this.getStoredToken('local') ?? this.getStoredToken('session');
            if (stored) {
                console.log('[FlamingoAuthService] Token not in signal, found in storage, updating signal');
                this.tokenSignal.set(stored);
                return stored;
            }
        }
        return token;
    }

    isLogged(): boolean {
        const token = this.getToken();
        if (!token) {
            return false;
        }
        // Check if token is expired
        try {
            const isExpired = this.jwtHelper.isTokenExpired(token);
            if (isExpired) {
                console.warn('[FlamingoAuthService] Token is expired in isLogged()');
                this.clearTokens();
                return false;
            }
            return true;
        } catch {
            console.warn('[FlamingoAuthService] Unable to validate token in isLogged()');
            this.clearTokens();
            return false;
        }
    }

    isTokenValid(): boolean {
        const token = this.getToken();
        if (!token) {
            return false;
        }
        try {
            return !this.jwtHelper.isTokenExpired(token);
        } catch {
            return false;
        }
    }

    setToken(token: string, options?: { rememberMe?: boolean; refreshToken?: string }): void {
        console.log('[FlamingoAuthService] Setting token, rememberMe:', options?.rememberMe);
        const scope: StorageScope = options?.rememberMe ? 'local' : 'session';
        this.persist(scope, this.tokenKey, token);

        if (options?.refreshToken) {
            this.persist(scope, this.refreshTokenKey, options.refreshToken);
        }

        this.tokenSignal.set(token);
        console.log('[FlamingoAuthService] Token set, verifying:', {
            signalValue: this.tokenSignal(),
            storedLocal: localStorage.getItem(this.tokenKey),
            storedSession: sessionStorage.getItem(this.tokenKey),
            isLogged: this.loggedIn()
        });
    }

    clearTokens(): void {
        this.remove('local', this.tokenKey);
        this.remove('session', this.tokenKey);
        this.remove('local', this.refreshTokenKey);
        this.remove('session', this.refreshTokenKey);
        this.tokenSignal.set(null);
    }

    logout(): void {
        this.clearTokens();
        this.remove('local', this.navigationKey);
        this.remove('session', this.navigationKey);
    }

    hasUserPermissions(required: string | string[]): boolean {
        const roles = this.user()?.roles ?? [];
        const requiredRoles = Array.isArray(required) ? required : [required];
        return requiredRoles.every(role => roles.includes(role));
    }

    setNavigation(navigation: unknown, rememberMe?: boolean): void {
        const scope: StorageScope = rememberMe ? 'local' : 'session';
        this.persist(scope, this.navigationKey, JSON.stringify(navigation));
    }

    getNavigation(): unknown {
        const stored = this.getStoredValue(this.navigationKey);
        if (!stored) {
            return null;
        }

        try {
            return JSON.parse(stored);
        } catch {
            console.warn('[FlamingoAuthService] Unable to parse stored navigation');
            return null;
        }
    }

    private persist(scope: StorageScope, key: string, value: string): void {
        this.remove(scope, key);
        this.store(scope, key, value);
    }

    private store(scope: StorageScope, key: string, value: string): void {
        const storage = scope === 'local' ? localStorage : sessionStorage;
        storage.setItem(key, value);
    }

    private remove(scope: StorageScope, key: string): void {
        const storage = scope === 'local' ? localStorage : sessionStorage;
        storage.removeItem(key);
    }

    private getStoredToken(scope: StorageScope): string | null {
        const storage = scope === 'local' ? localStorage : sessionStorage;
        return storage.getItem(this.tokenKey);
    }

    private getStoredValue(key: string): string | null {
        return localStorage.getItem(key) ?? sessionStorage.getItem(key);
    }
}


